/**
 * The fetchers, one tab each, so only one fetcher's fields are on screen.
 *
 * Each tab carries a coloured DOT for its status, not the full badge: five
 * badges made the strip wrap and buried which tab was open. The word is on the
 * dot's tooltip and in the open card's header. The children are the page's own
 * `FetcherCard`s; the open one is shown with its setup already open. The last
 * tab picked is remembered in this browser.
 */
import { Children, cloneElement, isValidElement, useState, type ReactElement, type ReactNode } from 'react';
import type { FetcherStatus } from '../../types';
import { SubTabs } from '../common/Tabs';
import { STATUS_WORDS } from './Badges';

type CardProps = { title: string; fetcher?: FetcherStatus; enabled?: boolean; alwaysOpen?: boolean };

const KEY = 'jcseo.network.fetcherTab';

const DOT: Record<FetcherStatus['status'], string> = {
  ready: 'bg-green-500',
  needs_setup: 'bg-gray-400 dark:bg-gray-500',
  unavailable: 'bg-red-500',
  off: 'border border-gray-400 dark:border-gray-500',
};

function StatusDot({ status }: { status: FetcherStatus['status'] }) {
  return (
    <span
      className={`inline-block h-2 w-2 rounded-full ${DOT[status] ?? DOT.needs_setup}`}
      title={STATUS_WORDS[status]}
      aria-label={STATUS_WORDS[status]}
      role="img"
    />
  );
}

function remembered(): string | null {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export default function FetcherTabs({ children }: { children: ReactNode }) {
  const cards = Children.toArray(children).filter(isValidElement) as ReactElement<CardProps>[];
  const titles = cards.map((c) => c.props.title);
  const [picked, setPicked] = useState<string | null>(remembered);
  const current = picked && titles.includes(picked) ? picked : titles[0];
  const pick = (title: string) => {
    setPicked(title);
    try {
      localStorage.setItem(KEY, title);
    } catch {
      // Not remembered; the tab still changes.
    }
  };
  const shown = cards.find((c) => c.props.title === current);
  return (
    <div>
      <SubTabs
        className="mb-3"
        active={current}
        onChange={pick}
        tabs={cards.map((c) => {
          const { title, fetcher, enabled } = c.props;
          const status = enabled === false ? 'off' : fetcher?.status;
          return {
            key: title,
            label: title,
            hint: status ? `${title}: ${STATUS_WORDS[status]}` : undefined,
            badge: status ? <StatusDot status={status} /> : undefined,
          };
        })}
      />
      {shown && cloneElement(shown, { alwaysOpen: true })}
    </div>
  );
}
